/**
 * NNADD Configurador — o estado da página (U7).
 *
 * A página (`assets/nnadd-configurador.js`) guarda as escolhas do cliente num
 * objeto simples e muda-o só por `aplicar(estado, acao)`. Tudo o que decide
 * (transições de peça, disponibilidade dos estilos, razão do botão bloqueado,
 * propriedades da linha) está aqui, sem DOM e sem rede, para se testar no Node
 * (tools/coordinates/test/configurador-estado.test.js).
 *
 * Sem imports (como `assets/nnadd-configurador-design.js`): as dependências
 * chegam num objeto injetado,
 *
 *   criarEstado({ conf, desenho, glifos, mapas, formatarDMS, lingua, aleatorio })
 *
 * - `conf`: o módulo `assets/nnadd-configurador-design.js` (PECAS, TINTAS,
 *   transicaoPeca, mudarCor...);
 * - `desenho`: `conf.criarDesenho(...)` (validarDescritor, formaMapa, pedidoMapa,
 *   pedidoDados, nomeParaDesenho);
 * - `glifos`: `assets/nnadd-configurador-glifos.js` (validarFrase, FRASES_INICIAIS,
 *   FRASE_POR_OMISSAO);
 * - `mapas`: `assets/nnadd-mapas.js` (desenharMapa, RAIOS_M, ESTILOS...);
 * - `formatarDMS`: de `assets/nnadd-coordinates-design.js`;
 * - opcionais: `pecas` e `pecasAtivas`, para testar com outra tabela (AE1);
 *   `lingua`, a língua da página (por omissão 'en'), para a frase inicial;
 *   `aleatorio`, o gerador do sorteio da frase (por omissão `Math.random`).
 *
 * ===== O estado
 *
 * `{ peca, cor, tamanho, tinta, memoria, variante, varianteCostas, disposicao,
 *    composicao, estilo, estiloCostas, lugares: [lugar|null, lugar|null], frase,
 *    fraseIndice, fraseMudada, qr, historia, publicar, confirmado, mapas }`
 * - `lugar`: `{ lat, lng, local }`, coordenadas já às 6 casas;
 * - `estilo` fica guardado mesmo em "só coordenadas" (o descritor leva null);
 *   é também "o último estilo usado" ao entrar numa disposição que exige mapa;
 * - `disposicao`: uma de `conf.DISPOSICOES` (por omissão 'frente'; plano de
 *   2026-10-22, KTD1). 'frente_pequena_costas' tem sempre a composição 'mapa';
 *   'frente_costas' tem sempre uma composição com mapa e `estiloCostas`;
 * - `estiloCostas`: o estilo do mapa das costas em 'frente_costas', diferente
 *   de `estilo`. Fica guardado (e no rascunho) noutras disposições, mas o
 *   descritor só o leva em 'frente_costas';
 * - `varianteCostas`: se a variante que o tema escolheu é uma "+ costas"
 *   (KTD3). A variante paga manda (KTD5): quando chega uma variante cujo ter
 *   costas diverge da disposição, a disposição muda; enquanto divergirem, a
 *   compra fica bloqueada (`variante_disposicao`). `varianteAlvo(estado)` diz
 *   a variante que a página deve pedir ao tema;
 * - `frase` é o texto do campo. Cada visita começa com uma de
 *   `glifos.FRASES_INICIAIS`, sorteada (`fraseIndice`) e na língua da página;
 *   enquanto `fraseMudada` for false, o rascunho repõe-se pelo índice e a página
 *   noutra língua mostra a tradução. O que o cliente escrever (ou apagar)
 *   fica, com `fraseMudada: true`;
 * - `mapas`: os dados de mapa por chave (estilo + pontos), com `estado`
 *   'a_carregar' | 'pronto' | 'indisponivel' | 'vazio' | 'falhou'. Cada lado
 *   com mapa tem a sua chave (KTD6): em 'frente_costas' são duas (os mesmos
 *   lugares, `estilo` à frente e `estiloCostas` atrás); nas outras há no
 *   máximo uma, no lado onde o mapa fica.
 *
 * `aplicar` devolve `{ estado, avisos }`, com `avisos` `[{ campo, motivo }]`:
 * - `{ campo: 'cor' | 'tinta' | 'estilo', motivo: 'reposta' }` (AE1);
 * - `{ campo: 'composicao', motivo: 'mapa_falhou' | 'mapa_vazio' |
 *   'mapa_indisponivel' | 'mapa_latitude' }` — o estilo deixou de existir no
 *   lugar e a composição passou a só coordenadas (AE3, KTD12);
 * - `{ campo: 'estilo', motivo: 'mapa_…' }` — o mesmo numa disposição que
 *   exige mapa ('frente_pequena_costas', 'frente_costas'): passa a outro
 *   estilo que exista (sem nenhum, fica e a compra espera);
 * - `{ campo: 'estiloCostas', motivo: 'mapa_…' | 'reposta' }` — o estilo das
 *   costas deixou de existir nos lugares (ou ficou igual ao da frente) e passou
 *   ao primeiro disponível diferente;
 * - `{ campo: 'disposicao', motivo: 'variante' }` — a variante do tema mudou a
 *   disposição (KTD5);
 * - `{ campo: 'confirmar', motivo: 'mudou' }` — a peça mudou depois de confirmada.
 *
 * As ações: 'peca', 'variante' (com `costas`, de `lerOpcaoPeca`; sem `costas`, a
 * disposição fica), 'disposicao',
 * 'tinta', 'composicao', 'estilo', 'estiloCostas' (com `estilo`), 'lugar',
 * 'frase', 'qr', 'historia', 'publicar', 'confirmar', 'mapa_a_carregar',
 * 'mapa_carregado'. Os mapas carregam-se só a pedido: `pedidosCarga(estado)`
 * diz os que faltam (`[{ lado, chave, pedido }]`), e a página chama o
 * carregador de U4 para cada um.
 */

const dono = (o, k) => typeof k === 'string' && o != null && Object.prototype.hasOwnProperty.call(o, k);

/**
 * A Shopify traduz os valores das opções (em /pt, "Black" chega como "Preto").
 * Além das chaves e dos nomes de `ETIQUETAS_CORES`, os nomes usados nas
 * traduções da loja (os mesmos de assets/nnadd-coordinates.js).
 */
export const ALIASES_COR = Object.freeze({
  Black: ['Preto', 'Preta', 'Noir', 'Schwarz'],
  White: ['Branco', 'Branca', 'Blanc', 'Weiß', 'Weiss'],
  Sand: ['Areia', 'Sable', 'Sand', 'Bone'],
  Navy: ['Azul-marinho', 'Bleu marine', 'Marine', 'Marineblau', 'Navy blue'],
  'Military Green': ['Verde-militar', 'Vert militaire', 'Militärgrün', 'Military green'],
});

/** Para comparar nomes: sem acentos, minúsculas, hífenes e espaços iguais. */
export function normalizarNome(valor) {
  return String(valor ?? '')
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .toLowerCase()
    .replace(/[\s_-]+/g, ' ')
    .trim();
}

const simples = (v) => normalizarNome(v).replace(/[^a-z0-9]/g, '');

/** A chave de cor (valor Color em inglês) para o valor da opção em qualquer língua da loja, ou null. */
export function resolverCor(valor, etiquetasCores) {
  const alvo = normalizarNome(valor);
  if (!alvo) return null;
  for (const [chave, etiquetas] of Object.entries(etiquetasCores ?? {})) {
    const nomes = [chave, ...Object.values(etiquetas ?? {}), ...(ALIASES_COR[chave] ?? [])];
    if (nomes.some((n) => normalizarNome(n) === alvo)) return chave;
  }
  return null;
}

/** A chave de peça para o valor da opção Peça (a chave ou o nome numa das línguas), ou null. */
export function resolverPeca(valor, pecas) {
  const alvo = simples(valor);
  if (!alvo) return null;
  return (
    Object.keys(pecas ?? {}).find(
      (k) => simples(k) === alvo || Object.values(pecas[k].etiquetas ?? {}).some((e) => simples(e) === alvo)
    ) ?? null
  );
}

/**
 * Os sufixos "+ costas" da opção Peça (KTD3): o valor base em inglês e as
 * traduções da loja, já normalizados (sem acentos, minúsculas).
 */
const SUFIXO_COSTAS = /^(.*?)\s*\+\s*(back|costas|dos|rucken|ruecken)$/;
/**
 * Os sufixos "+ manga" da opção Peça (linha Terra, plano de 2026-10-24, U2):
 * à parte das costas, para o `conf-3` não mudar.
 */
const SUFIXO_MANGA = /^(.*?)\s*\+\s*(sleeve|manga|manche|armel|aermel)$/;

/**
 * O leitor partilhado da opção Peça (KTD3), usado pela página e pelo PC:
 * `{ peca, costas }` para "T-shirt", "T-shirt + back", "Hoodie + costas",
 * "Sweat-shirt + dos", "T-Shirt + Rücken"... (o nome da peça em qualquer
 * língua), `{ peca, costas: false, manga: true }` para "T-shirt + sleeve",
 * "T-shirt + manga", "T-shirt + manche", "T-Shirt + Ärmel"..., ou null se a
 * peça não estiver em `pecas`.
 */
export function lerOpcaoPeca(valor, pecas) {
  const nome = normalizarNome(valor);
  const manga = SUFIXO_MANGA.exec(nome);
  if (manga) {
    const peca = resolverPeca(manga[1], pecas);
    return peca ? { peca, costas: false, manga: true } : null;
  }
  const m = SUFIXO_COSTAS.exec(nome);
  const peca = resolverPeca(m ? m[1] : nome, pecas);
  return peca ? { peca, costas: Boolean(m) } : null;
}

/** O valor visível de `Disposição` na linha (KTD4), por língua. */
export const ETIQUETAS_DISPOSICOES = Object.freeze({
  frente: Object.freeze({ 'pt-PT': 'Só frente', en: 'Front only', fr: 'Devant seulement', de: 'Nur vorne' }),
  frente_pequena_costas: Object.freeze({ 'pt-PT': 'Frente pequena + costas', en: 'Small front + back', fr: 'Petit devant + dos', de: 'Kleine Vorderseite + Rücken' }),
  costas: Object.freeze({ 'pt-PT': 'Só costas', en: 'Back only', fr: 'Dos seulement', de: 'Nur Rücken' }),
  frente_costas: Object.freeze({ 'pt-PT': 'Frente + costas', en: 'Front + back', fr: 'Devant + dos', de: 'Vorne + Rücken' }),
});

/** O tamanho (um de `tamanhos`) para o valor da opção Size, ou null. */
export function resolverTamanho(valor, tamanhos) {
  const alvo = String(valor ?? '').trim().toUpperCase();
  return (tamanhos ?? []).find((t) => t === alvo) ?? null;
}

/** O nome na língua pedida, numa tabela de nomes por língua (a língua base serve; senão inglês). */
export function etiquetaNaLingua(etiquetas, lingua) {
  if (!etiquetas) return '';
  if (dono(etiquetas, lingua)) return etiquetas[lingua];
  const base = String(lingua ?? '').split('-')[0];
  const chave = Object.keys(etiquetas).find((k) => k.split('-')[0] === base);
  return etiquetas[chave ?? 'en'] ?? '';
}

/** Arredonda às 6 casas como `arredondar6` da Coordinates (sem -0). */
function arredondar6(graus) {
  const r = Math.round(Math.abs(graus) * 1e6) / 1e6;
  return graus < 0 && r !== 0 ? -r : r;
}

const normalizarLongitude = (lng) => ((((lng + 180) % 360) + 360) % 360) - 180;
const seis = (v) => v.toFixed(6);

export const LOCAL_MAX = 80;
export const HISTORIA_MAX = 500;

/** O parâmetro do URL que leva a peça de um link "copiar link" (plano de tráfego, 0.3). */
export const PARAM_PARTILHA = 'design';

/**
 * O valor de `?design=` de um link "copiar link": o rascunho sem a história e
 * sem a autorização de a publicar, que são de quem a escreveu, em JSON e
 * base64url (cabe no URL sem escapes).
 */
export function codificarPartilha(rascunho) {
  const { historia, publicar, ...resto } = rascunho ?? {};
  let binario = '';
  for (const b of new TextEncoder().encode(JSON.stringify(resto))) binario += String.fromCharCode(b);
  return btoa(binario).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/**
 * O rascunho de um `?design=`, ou null se o texto não for um. Cada valor é
 * validado depois por `restaurar`, com as mesmas regras das ações.
 */
export function lerPartilha(texto) {
  if (typeof texto !== 'string' || !texto || texto.length > 4000 || !/^[A-Za-z0-9_-]+$/.test(texto)) return null;
  try {
    const binario = atob(texto.replace(/-/g, '+').replace(/_/g, '/'));
    const json = new TextDecoder('utf-8', { fatal: true }).decode(Uint8Array.from(binario, (c) => c.charCodeAt(0)));
    const r = JSON.parse(json);
    if (!r || typeof r !== 'object' || Array.isArray(r)) return null;
    delete r.historia;
    delete r.publicar;
    return r;
  } catch {
    return null;
  }
}

/**
 * A peça de exemplo, quando o configurador abre sem peça nenhuma (sem link "copiar link", sem
 * rascunho da sessão e sem `?lugar=`); a secção da página inicial usa as mesmas terras. Uma terra ao
 * calhas, de qualquer país (a marca é sobre a terra de cada um, não só a portuguesa), com
 * coordenadas e mapa de ruas, e a frase sorteada. O cliente muda a terra e fica com o resto. Um
 * rascunho como os outros, reposto por `restaurar`. As coordenadas vêm do Nominatim (2026-10-24);
 * onde o Nominatim dá o centro do concelho ou fica fora do centro, o centro da cidade.
 */
export const EXEMPLO_TERRAS = Object.freeze([
  ['Guimarães', 41.441768, -8.295571],
  ['Porto', 41.150219, -8.61035],
  ['Funchal', 32.64965, -16.908678],
  ['Napoli', 40.835885, 14.248768],
  ['Palermo', 38.111227, 13.352443],
  ['Bologna', 44.49382, 11.342633],
  ['Marseille', 43.296399, 5.377789],
  ['Lyon', 45.757814, 4.832011],
  ['Sevilla', 37.38863, -5.99534],
  ['A Coruña', 43.3623, -8.4115],
  ['Kraków', 50.0614, 19.9366],
  ['Gdańsk', 54.348291, 18.654023],
  ['Cork', 51.898514, -8.472642],
  ['Glasgow', 55.861155, -4.250169],
  ['Liverpool', 53.4084, -2.9916],
  ['Thessaloniki', 40.640317, 22.935272],
  ['Casablanca', 33.594514, -7.620028],
  ['Dakar', 14.693425, -17.447938],
  ['Praia', 14.916281, -23.50951],
  ['Salvador', -12.98225, -38.481277],
  ['Medellín', 6.2442, -75.5812],
  ['Newark', 40.735657, -74.172367],
  ['Montréal', 45.503182, -73.569806],
]);

/** O rascunho de exemplo: uma terra ao calhas, em qualquer língua. */
export function rascunhoDeExemplo(aleatorio = Math.random) {
  const [local, lat, lng] = EXEMPLO_TERRAS[Math.floor(aleatorio() * EXEMPLO_TERRAS.length)];
  return { v: 2, lugares: [{ lat, lng, local }, null], composicao: 'coordenadas_mapa', estilo: 'ruas' };
}

/** Motivos do carregador (U4) que dizem respeito ao lugar, não ao serviço: não se tenta outra vez. */
const MOTIVOS_DO_LUGAR = new Set(['latitude', 'sem_regiao', 'regiao_sem_ponto', 'estilo_desconhecido', 'osm_id_invalido', 'versao_invalida']);
const ESTADOS_SEM_MAPA = new Set(['indisponivel', 'vazio', 'falhou']);

/** Um lugar válido às 6 casas, ou null. */
function lerLugar(l) {
  if (!l || typeof l !== 'object') return null;
  const { lat, lng } = l;
  if (typeof lat !== 'number' || typeof lng !== 'number' || !Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  if (Math.abs(lat) > 90) return null;
  const local = typeof l.local === 'string' ? l.local.normalize('NFC').trim().replace(/\s+/g, ' ') : '';
  if (!local || [...local].length > LOCAL_MAX || /[<>]/.test(local) || /[\p{Cc}\p{Cf}]/u.test(local)) return null;
  return { lat: arredondar6(lat), lng: arredondar6(normalizarLongitude(lng)), local };
}

/**
 * Cria a máquina de estado com as dependências injetadas. Ver o cabeçalho.
 */
export function criarEstado(deps = {}) {
  const { conf, desenho, glifos, mapas, formatarDMS } = deps;
  if (!conf || typeof conf.transicaoPeca !== 'function' || !conf.PECAS) throw new Error('nnadd-configurador-estado: falta deps.conf');
  if (!desenho || typeof desenho.validarDescritor !== 'function') throw new Error('nnadd-configurador-estado: falta deps.desenho');
  if (!glifos || typeof glifos.validarFrase !== 'function') throw new Error('nnadd-configurador-estado: falta deps.glifos');
  if (!mapas || typeof mapas.desenharMapa !== 'function') throw new Error('nnadd-configurador-estado: falta deps.mapas');
  if (typeof formatarDMS !== 'function') throw new Error('nnadd-configurador-estado: falta deps.formatarDMS');

  const PECAS = deps.pecas ?? conf.PECAS;
  const ATIVAS = [...(deps.pecasAtivas ?? conf.PECAS_ATIVAS)];
  const ESTILOS = [...(mapas.ESTILOS ?? [])];
  const COMPOSICOES = [...conf.COMPOSICOES];
  const DISPOSICOES = [...(conf.DISPOSICOES ?? ['frente'])];
  const TAMANHOS = [...conf.TAMANHOS];
  const FRASE_POR_OMISSAO = glifos.FRASE_POR_OMISSAO ?? '';
  const FRASES = [...(glifos.FRASES_INICIAIS ?? [])];
  const LINGUA = typeof deps.lingua === 'string' && deps.lingua ? deps.lingua : 'en';
  const aleatorio = typeof deps.aleatorio === 'function' ? deps.aleatorio : Math.random;

  const comMapa = (c) => c === 'coordenadas_mapa' || c === 'mapa';
  const disposicaoDe = (e) => (DISPOSICOES.includes(e.disposicao) ? e.disposicao : 'frente');
  /** Se a disposição leva alguma coisa nas costas (e por isso a variante "+ costas"; `conf.temCostas`). */
  const temCostas = (e) => conf.temCostas(disposicaoDe(e));
  /** As disposições em que o mapa do `estilo` é obrigatório (R3, R5). */
  const exigeMapa = (e) => disposicaoDe(e) === 'frente_pequena_costas' || disposicaoDe(e) === 'frente_costas';
  /** As disposições em que o desenho completo, com a frase, vai nas costas (R3, R4). */
  const fraseNasCostas = (e) => disposicaoDe(e) === 'frente_pequena_costas' || disposicaoDe(e) === 'costas';
  const comCostasCompletas = (e) => disposicaoDe(e) === 'frente_costas';
  const duasFrente = (e) => comMapa(e.composicao) && e.estilo === 'duas_terras';
  /** Se a escolha usa os dois lugares ("as duas terras" num dos lados). */
  const duas = (e) => duasFrente(e) || (comCostasCompletas(e) && e.estiloCostas === 'duas_terras');
  const limiteFrase = (e) => PECAS[e.peca]?.limiteFrase ?? 30;
  const aceitaQR = (e) => PECAS[e.peca]?.aceitaQR !== false;
  /** Os lugares que a escolha atual usa (dois em "as duas terras"), sem os vazios. */
  const lugaresEmUso = (e) => e.lugares.slice(0, duas(e) ? 2 : 1).filter(Boolean);

  // ===== Mapas

  const lugaresDoEstilo = (e, estilo) => e.lugares.slice(0, estilo === 'duas_terras' ? 2 : 1);

  /** A chave dos dados de mapa de um estilo nos lugares atuais (null se faltar um lugar). */
  function chavePara(e, estilo) {
    const ls = lugaresDoEstilo(e, estilo);
    if (ls.some((l) => !l)) return null;
    return [estilo, ...ls.map((l) => `${seis(l.lat)},${seis(l.lng)}`)].join('|');
  }
  const chaveMapa = (e) => (comMapa(e.composicao) ? chavePara(e, e.estilo) : null);
  /** Os dados de mapa de um estilo nos lugares atuais (undefined sem lugar ou por carregar). */
  const mapaDe = (e, estilo) => e.mapas[chavePara(e, estilo) ?? ''];

  /**
   * Os mapas da escolha atual, um por lado (KTD6): `[{ lado, estilo, campo }]`.
   * O de `estilo` fica nas costas em 'costas' e 'frente_pequena_costas'; em
   * 'frente_costas' há também o de `estiloCostas`, atrás. `campo` é o
   * controlo do estilo desse mapa.
   */
  function mapasEmUso(e) {
    const r = [];
    const disposicao = disposicaoDe(e);
    if (comMapa(e.composicao)) {
      r.push({ lado: disposicao === 'costas' || disposicao === 'frente_pequena_costas' ? 'costas' : 'frente', estilo: e.estilo, campo: 'estilo' });
    }
    if (disposicao === 'frente_costas' && e.estiloCostas) r.push({ lado: 'costas', estilo: e.estiloCostas, campo: 'estiloCostas' });
    return r;
  }
  const chaveCostas = (e) => (comCostasCompletas(e) && e.estiloCostas ? chavePara(e, e.estiloCostas) : null);

  function lerChave(chave) {
    const [estilo, ...pts] = String(chave).split('|');
    return { estilo, pontos: pts.map((p) => p.split(',').map(Number)).map(([lat, lng]) => ({ lat, lng })) };
  }

  /** A regra dos 85° (KTD12) para os lugares já escolhidos. */
  function latitudeOk(e, estilo) {
    return lugaresDoEstilo(e, estilo)
      .filter(Boolean)
      .every((l) => mapas.disponibilidadeEstilos(l.lat)[estilo] !== false);
  }

  const proximos = (a, b) => mapas.distanciaM({ lat: a.lat, lon: a.lng }, { lat: b.lat, lon: b.lng }) < (mapas.DISTANCIA_MINIMA_M ?? 1000);

  /**
   * Se um estilo existe nos lugares atuais: `{ disponivel, motivo }`. Um
   * serviço que falhou conta como disponível, com `motivo: 'falhou'`, para se
   * poder tentar outra vez; 'latitude', 'indisponivel' e 'vazio' não.
   */
  function estiloDisponivel(e, estilo) {
    if (!latitudeOk(e, estilo)) return { disponivel: false, motivo: 'latitude' };
    const k = chavePara(e, estilo);
    const m = k ? e.mapas[k] : null;
    if (m && (m.estado === 'indisponivel' || m.estado === 'vazio')) return { disponivel: false, motivo: m.estado };
    if (m?.estado === 'falhou') return { disponivel: true, motivo: 'falhou' };
    return { disponivel: true, motivo: null };
  }

  /**
   * Um estilo que se pode escolher sem esperar uma falha conhecida, segundo `disponivel`
   * (`estiloDisponivel` à frente, `estiloCostasDisponivel` nas costas).
   */
  const usavelSegundo = (disponivel) => (e, s) => disponivel(e, s).disponivel && mapaDe(e, s)?.estado !== 'falhou';
  const usavel = usavelSegundo(estiloDisponivel);

  /**
   * Se um estilo serve para as costas de 'frente_costas' (R5, AE2):
   * `{ disponivel, motivo }`. Além do que conta para a frente, tem de ser
   * diferente do da frente ('igual'), "as duas terras" precisa do segundo
   * lugar ('falta_lugar2'), e o descritor com ele tem de ser válido para o
   * módulo de desenho ('combinacao').
   */
  function estiloCostasDisponivel(e, estilo) {
    if (estilo === e.estilo) return { disponivel: false, motivo: 'igual' };
    if (estilo === 'duas_terras' && !e.lugares[1]) return { disponivel: false, motivo: 'falta_lugar2' };
    const base = estiloDisponivel(e, estilo);
    if (!base.disponivel || !e.lugares[0]) return base;
    const d = descritor({ ...e, disposicao: 'frente_costas', composicao: comMapa(e.composicao) ? e.composicao : 'coordenadas_mapa', estiloCostas: estilo });
    const { erros } = desenho.validarDescritor(d, { pecasAtivas: Object.keys(PECAS) });
    if (erros.estiloCostas || erros.pontos || erros.nomes) return { disponivel: false, motivo: 'combinacao' };
    return base;
  }
  const usavelCostas = usavelSegundo(estiloCostasDisponivel);

  /**
   * Em 'frente_costas', mantém `estiloCostas` se servir, senão passa ao
   * primeiro estilo que sirva (null sem nenhum). `aviso` é o motivo do aviso
   * quando um estilo escolhido muda (sem aviso quando não havia nenhum).
   */
  function escolherEstiloCostas(e, avisos, aviso = 'reposta') {
    if (!comCostasCompletas(e)) return e;
    if (e.estiloCostas && usavelCostas(e, e.estiloCostas)) return e;
    const novo = ESTILOS.find((s) => usavelCostas(e, s)) ?? null;
    if (novo === e.estiloCostas) return e;
    if (e.estiloCostas) avisos.push({ campo: 'estiloCostas', motivo: aviso });
    return { ...e, estiloCostas: novo };
  }

  /** Se o estilo das costas deixou de existir nos lugares, passa a outro, com aviso (KTD6). */
  function reverterCostas(e, avisos) {
    if (!comCostasCompletas(e) || !e.estiloCostas || !e.lugares[0]) return e;
    const r = estiloCostasDisponivel(e, e.estiloCostas);
    const m = mapaDe(e, e.estiloCostas);
    let motivo = null;
    if (!r.disponivel) motivo = ['latitude', 'indisponivel', 'vazio'].includes(r.motivo) ? `mapa_${r.motivo}` : 'reposta';
    else if (m?.estado === 'falhou') motivo = 'mapa_falhou';
    if (!motivo) return e;
    const x = escolherEstiloCostas({ ...e, estiloCostas: null }, avisos);
    avisos.push({ campo: 'estiloCostas', motivo });
    return desconfirmar(x, avisos);
  }

  /** Classifica a carga de U4 e desenha o mapa uma vez (forma de "só mapa") para saber se está vazio. */
  function classificar(chave, carga) {
    if (!carga || carga.ok !== true) {
      const motivo = carga?.motivo ?? 'rede';
      return { estado: MOTIVOS_DO_LUGAR.has(motivo) ? 'indisponivel' : 'falhou', motivo };
    }
    const { estilo, pontos } = lerChave(chave);
    let r;
    try {
      r = mapas.desenharMapa(desenho.pedidoMapa({ composicao: 'mapa', estilo, pontos }, carga.dados ?? {}));
    } catch {
      return { estado: 'falhou', motivo: 'desenho' };
    }
    const base = { dados: carga.dados ?? {}, versaoDados: carga.versaoUsada ?? null, osm: carga.osmId ?? null };
    if (!r.disponivel) return { ...base, estado: 'indisponivel', motivo: r.motivo };
    if (r.vazio) return { ...base, estado: 'vazio', motivo: 'vazio' };
    return { ...base, estado: 'pronto', motivo: null };
  }

  /** Se o estilo escolhido deixou de existir no lugar, passa a só coordenadas, com aviso (AE3, KTD12). */
  function reverterSemMapa(e, avisos) {
    if (!comMapa(e.composicao)) return e;
    const ls = lugaresDoEstilo(e, e.estilo);
    if (!ls[0]) return e;
    let motivo = null;
    if (!latitudeOk(e, e.estilo)) motivo = 'latitude';
    else {
      const k = chaveMapa(e);
      const m = k ? e.mapas[k] : null;
      if (m && ESTADOS_SEM_MAPA.has(m.estado)) motivo = m.estado;
    }
    if (!motivo) return e;
    if (exigeMapa(e)) {
      // Aqui a composição não pode ficar sem mapa: passa a outro estilo, se houver.
      avisos.push({ campo: 'estilo', motivo: `mapa_${motivo}` });
      const alternativa = ESTILOS.find((s) => s !== e.estilo && usavel(e, s));
      const x = alternativa ? escolherEstiloCostas({ ...e, estilo: alternativa }, avisos) : e;
      return desconfirmar(x, avisos);
    }
    avisos.push({ campo: 'composicao', motivo: `mapa_${motivo}` });
    return desconfirmar({ ...e, composicao: 'coordenadas' }, avisos);
  }

  /** Uma escolha explícita de mapa volta a tentar um serviço que tinha falhado (em `k`, por omissão o da frente). */
  function esquecerFalha(e, k = chaveMapa(e)) {
    if (!k || e.mapas[k]?.estado !== 'falhou') return e;
    const mapasNovos = { ...e.mapas };
    delete mapasNovos[k];
    return { ...e, mapas: mapasNovos };
  }

  /**
   * Se o estilo guardado não existe aqui (ou o serviço falhou), passa ao
   * primeiro que exista e não tenha falhado, com aviso; sem nenhum, fica (e
   * tenta outra vez).
   */
  function comEstiloUsavel(e, avisos) {
    if (!e.lugares[0] || usavel(e, e.estilo)) return e;
    const alternativa = ESTILOS.find((s) => usavel(e, s) && !(comCostasCompletas(e) && s === e.estiloCostas));
    if (!alternativa) return e;
    avisos.push({ campo: 'estilo', motivo: 'reposta' });
    return { ...e, estilo: alternativa };
  }

  /**
   * Muda a disposição (KTD1), com as transições de U3: 'frente_pequena_costas'
   * passa a composição a 'mapa' (com o último estilo usado, senão o primeiro
   * que exista); 'frente_costas' passa "só coordenadas" a 'coordenadas_mapa'
   * e escolhe o estilo das costas (o guardado se servir, senão o primeiro
   * disponível diferente). Voltar a 'frente' guarda `estiloCostas`. Desconfirma.
   */
  function mudarDisposicao(e, disposicao, avisos) {
    if (!DISPOSICOES.includes(disposicao) || disposicao === disposicaoDe(e)) return e;
    let x = { ...e, disposicao };
    if (disposicao === 'frente_pequena_costas') x = { ...x, composicao: 'mapa' };
    if (disposicao === 'frente_costas' && !comMapa(x.composicao)) x = { ...x, composicao: 'coordenadas_mapa' };
    if (exigeMapa(x)) {
      x = comEstiloUsavel(x, avisos);
      x = escolherEstiloCostas(x, avisos);
      x = esquecerFalha(esquecerFalha(x), chaveCostas(x));
    }
    return desconfirmar(x, avisos);
  }

  function desconfirmar(e, avisos) {
    if (!e.confirmado) return e;
    avisos.push({ campo: 'confirmar', motivo: 'mudou' });
    return { ...e, confirmado: false };
  }

  // ===== Estado inicial

  const indiceValido = (i) => Number.isInteger(i) && i >= 0 && i < FRASES.length;

  /** Sorteia o índice da frase inicial (null sem lista). */
  function sortearIndice() {
    if (!FRASES.length) return null;
    const r = Number(aleatorio());
    const i = Number.isFinite(r) ? Math.floor(r * FRASES.length) : 0;
    return Math.min(Math.max(i, 0), FRASES.length - 1);
  }

  /** A frase da lista no índice `i`, na língua da página ('pt' é pt-PT; uma língua sem versão passa a inglês). */
  const fraseDaLista = (i) => (indiceValido(i) ? etiquetaNaLingua(FRASES[i], LINGUA) : FRASE_POR_OMISSAO);

  /** O estado inicial para a variante selecionada (chaves já resolvidas). */
  function inicial({ peca, cor, tamanho, costas } = {}) {
    const p = dono(PECAS, peca) ? peca : ATIVAS[0];
    const corOk = dono(PECAS[p]?.cores, cor);
    const t = resolverTamanho(tamanho, TAMANHOS);
    const fraseIndice = sortearIndice();
    const e = {
      peca: p,
      cor: corOk ? cor : null,
      tamanho: t,
      tinta: corOk ? conf.tintaPorOmissao(p, cor, PECAS) : null,
      memoria: {},
      variante: corOk && t && dono(PECAS, peca) ? 'ok' : 'desconhecida',
      varianteCostas: costas === true,
      disposicao: 'frente',
      composicao: 'coordenadas',
      estilo: ESTILOS[0] ?? 'ruas',
      estiloCostas: null,
      lugares: [null, null],
      frase: fraseDaLista(fraseIndice),
      fraseIndice,
      fraseMudada: false,
      qr: false,
      historia: '',
      publicar: false,
      confirmado: false,
      mapas: {},
    };
    // Uma variante "+ costas" (um link) abre em frente pequena + costas (AE3).
    return e.varianteCostas ? mudarDisposicao(e, 'frente_pequena_costas', []) : e;
  }

  // ===== Ações

  function aplicar(estado, acao = {}) {
    const avisos = [];
    let e = estado;
    switch (acao.tipo) {
      case 'peca': {
        if (!dono(PECAS, acao.peca) || !ATIVAS.includes(acao.peca) || acao.peca === e.peca) break;
        const r = conf.transicaoPeca(e, acao.peca, PECAS);
        avisos.push(...r.avisos);
        e = desconfirmar(r.estado, avisos);
        if (!aceitaQR(e)) e = { ...e, qr: false };
        break;
      }
      case 'variante': {
        const { peca, cor, tamanho } = acao;
        const antes = e;
        let variante = 'ok';
        if (peca !== undefined) {
          if (!dono(PECAS, peca)) variante = 'desconhecida';
          else if (peca !== e.peca) {
            const r = conf.transicaoPeca(e, peca, PECAS);
            // A cor vem da variante: o aviso de cor reposta só conta se a variante não a trouxer.
            avisos.push(...r.avisos.filter((a) => a.campo !== 'cor' || !cor));
            e = r.estado;
          }
        }
        if (cor === null || cor === undefined || !dono(PECAS[e.peca]?.cores, cor)) {
          variante = 'desconhecida';
          e = { ...e, cor: null };
        } else if (cor !== e.cor) {
          const r = conf.mudarCor(e, cor, PECAS);
          for (const a of r.avisos) if (!avisos.some((b) => b.campo === a.campo)) avisos.push(a);
          e = r.estado;
        }
        const t = resolverTamanho(tamanho, TAMANHOS);
        if (!t) variante = 'desconhecida';
        // Sem `costas` na ação (troca de cor ou de tamanho na página) não há
        // informação sobre as costas: fica o que já se sabia e a disposição não muda.
        const semCostas = acao.costas === undefined;
        const costas = semCostas ? e.varianteCostas : acao.costas === true;
        e = { ...e, tamanho: t, variante, varianteCostas: costas };
        if (e.peca !== antes.peca || e.cor !== antes.cor || e.tinta !== antes.tinta) e = desconfirmar(e, avisos);
        if (!aceitaQR(e)) e = { ...e, qr: false };
        // A variante paga manda na disposição, só quando diverge no ter costas (KTD5)
        // e a ação diz se a variante é "+ costas".
        if (!semCostas && costas !== temCostas(e) && (peca === undefined || dono(PECAS, peca))) {
          e = mudarDisposicao(e, costas ? 'frente_pequena_costas' : 'frente', avisos);
          avisos.push({ campo: 'disposicao', motivo: 'variante' });
        }
        break;
      }
      case 'disposicao': {
        e = mudarDisposicao(e, acao.disposicao, avisos);
        break;
      }
      case 'tinta': {
        if (!e.cor || !conf.tintasPermitidas(e.peca, e.cor, PECAS).includes(acao.tinta) || acao.tinta === e.tinta) break;
        e = desconfirmar({ ...e, tinta: acao.tinta }, avisos);
        break;
      }
      case 'composicao': {
        if (!COMPOSICOES.includes(acao.composicao) || acao.composicao === e.composicao) break;
        // Frente pequena + costas é só mapa; frente + costas leva sempre mapa à frente.
        if (disposicaoDe(e) === 'frente_pequena_costas' || (comCostasCompletas(e) && !comMapa(acao.composicao))) break;
        e = desconfirmar({ ...e, composicao: acao.composicao }, avisos);
        if (comMapa(e.composicao)) {
          // Sem nenhum estilo usável, tenta outra vez o guardado.
          e = esquecerFalha(comEstiloUsavel(e, avisos));
        }
        break;
      }
      case 'estilo': {
        if (!ESTILOS.includes(acao.estilo) || acao.estilo === e.estilo) break;
        e = { ...e, estilo: acao.estilo };
        if (comMapa(e.composicao)) e = desconfirmar(esquecerFalha(e), avisos);
        // Nas costas, outro estilo que não o da frente (R5).
        e = escolherEstiloCostas(e, avisos);
        break;
      }
      case 'estiloCostas': {
        if (!ESTILOS.includes(acao.estilo) || acao.estilo === e.estiloCostas) break;
        if (comCostasCompletas(e)) {
          if (!estiloCostasDisponivel(e, acao.estilo).disponivel) break;
          e = desconfirmar({ ...e, estiloCostas: acao.estilo }, avisos);
          e = esquecerFalha(e, chaveCostas(e));
        } else {
          if (acao.estilo === e.estilo) break;
          e = { ...e, estiloCostas: acao.estilo };
        }
        break;
      }
      case 'lugar': {
        const i = acao.indice === 1 ? 1 : 0;
        const lugar = acao.lugar === null ? null : lerLugar(acao.lugar);
        if (acao.lugar !== null && !lugar) break;
        const atual = e.lugares[i];
        if (atual && lugar && atual.lat === lugar.lat && atual.lng === lugar.lng && atual.local === lugar.local) break;
        if (!atual && !lugar) break;
        const lugares = [...e.lugares];
        lugares[i] = lugar;
        e = desconfirmar({ ...e, lugares }, avisos);
        e = reverterSemMapa(e, avisos);
        e = reverterCostas(e, avisos);
        break;
      }
      case 'frase': {
        if (typeof acao.frase !== 'string') break;
        // Uma frase escrita (ou apagada) pelo cliente fica, mesmo noutra língua.
        if (acao.frase === e.frase) {
          if (!e.fraseMudada) e = { ...e, fraseMudada: true };
          break;
        }
        e = desconfirmar({ ...e, frase: acao.frase, fraseMudada: true }, avisos);
        break;
      }
      case 'qr': {
        const qr = acao.qr === true && aceitaQR(e);
        if (qr === e.qr) break;
        e = desconfirmar({ ...e, qr }, avisos);
        break;
      }
      case 'historia': {
        if (typeof acao.historia !== 'string') break;
        e = { ...e, historia: acao.historia };
        break;
      }
      case 'publicar': {
        e = { ...e, publicar: acao.publicar === true };
        break;
      }
      case 'confirmar': {
        e = { ...e, confirmado: acao.confirmado === true };
        break;
      }
      case 'mapa_a_carregar': {
        if (typeof acao.chave !== 'string') break;
        e = { ...e, mapas: { ...e.mapas, [acao.chave]: { estado: 'a_carregar', motivo: null } } };
        break;
      }
      case 'mapa_carregado': {
        if (typeof acao.chave !== 'string') break;
        e = { ...e, mapas: { ...e.mapas, [acao.chave]: classificar(acao.chave, acao.carga) } };
        if (acao.chave === chaveMapa(e)) e = reverterSemMapa(e, avisos);
        if (acao.chave === chaveCostas(e)) e = reverterCostas(e, avisos);
        break;
      }
      default:
        break;
    }
    return { estado: e, avisos };
  }

  // ===== Leitura

  /**
   * Os pedidos para o carregador de U4 (`carregar(pedido)`), um por mapa da
   * escolha atual que falte carregar: `[{ lado, chave, pedido }]` (KTD6; dois
   * em 'frente_costas', os mesmos lugares com dois estilos). Em "as duas
   * terras" é a caixa da costa (`desenho.pedidoDados`), só com os dois
   * lugares afastados.
   */
  function pedidosCarga(e) {
    const r = [];
    for (const { lado, estilo } of mapasEmUso(e)) {
      const k = chavePara(e, estilo);
      if (!k || e.mapas[k] || !latitudeOk(e, estilo) || r.some((p) => p.chave === k)) continue;
      if (estilo === 'duas_terras') {
        if (proximos(e.lugares[0], e.lugares[1])) continue;
        r.push({ lado, chave: k, pedido: desenho.pedidoDados({ estilo, pontos: e.lugares.slice(0, 2) }) });
        continue;
      }
      const l = e.lugares[0];
      const pedido = { estilo, centro: { lat: l.lat, lon: l.lng } };
      const raio = mapas.RAIOS_M?.[estilo];
      if (raio !== undefined) pedido.raioM = raio;
      r.push({ lado, chave: k, pedido });
    }
    return r;
  }

  /** O primeiro pedido de `pedidosCarga`, ou null. */
  const pedidoCarga = (e) => pedidosCarga(e)[0] ?? null;

  /** O estado do mapa de um estilo nos lugares atuais. */
  function estadoDoEstilo(e, estilo) {
    if (!latitudeOk(e, estilo)) return { estado: 'indisponivel', motivo: 'latitude' };
    const k = chavePara(e, estilo);
    if (!k) return { estado: 'sem_lugar', motivo: null };
    const m = e.mapas[k];
    if (!m) {
      if (estilo === 'duas_terras' && proximos(e.lugares[0], e.lugares[1])) return { estado: 'sem_lugar', motivo: 'pontos_proximos' };
      return { estado: 'por_carregar', motivo: null };
    }
    return { estado: m.estado, motivo: m.motivo ?? null };
  }

  /**
   * O estado do mapa do `lado` ('frente' | 'costas'), ou, sem lado, o do
   * primeiro mapa da escolha que não esteja pronto ('pronto' se estiverem
   * todos; 'sem_mapa' sem nenhum).
   */
  function estadoMapa(e, lado = null) {
    const usados = mapasEmUso(e).filter((m) => lado === null || m.lado === lado);
    if (!usados.length) return { estado: 'sem_mapa', motivo: null };
    const estados = usados.map((m) => estadoDoEstilo(e, m.estilo));
    return estados.find((s) => s.estado !== 'pronto') ?? estados[0];
  }

  /** Os estilos com a disponibilidade nos lugares atuais (os indisponíveis ficam visíveis, desativados). */
  function estilos(e) {
    return ESTILOS.map((estilo) => ({ estilo, ...estiloDisponivel(e, estilo) }));
  }

  /** Os estilos das costas de 'frente_costas', com a disponibilidade (R5, AE2). */
  function estilosCostas(e) {
    return ESTILOS.map((estilo) => ({ estilo, ...estiloCostasDisponivel(e, estilo) }));
  }

  /** A variante que a página deve pedir ao tema: a peça, a cor, o tamanho e se é "+ costas" (KTD5). */
  function varianteAlvo(e) {
    return { peca: e.peca, cor: e.cor, tamanho: e.tamanho, costas: temCostas(e) };
  }

  /** A frase validada: `{ valida, frase, motivo?, caracter?, limite, restantes }`. */
  function fraseInfo(e) {
    const limite = limiteFrase(e);
    const r = glifos.validarFrase(e.frase ?? '', limite);
    const normalizada = String(e.frase ?? '').normalize('NFC').trim();
    return { ...r, frase: r.valida ? r.frase : normalizada, limite, restantes: limite - [...normalizada].length };
  }

  /**
   * O descritor de U5 (KTD4) para a escolha atual. `disposicao` só vai fora
   * de só frente, e `estiloCostas` só em 'frente_costas' (em falta = 'frente').
   */
  function descritor(e) {
    const ls = lugaresEmUso(e);
    const disposicao = disposicaoDe(e);
    return {
      versao: conf.VERSAO_CONFIGURADOR,
      ...(disposicao !== 'frente' ? { disposicao } : {}),
      peca: e.peca,
      cor: e.cor,
      tamanho: e.tamanho,
      tinta: e.tinta,
      composicao: e.composicao,
      estilo: comMapa(e.composicao) ? e.estilo : null,
      ...(disposicao === 'frente_costas' ? { estiloCostas: e.estiloCostas } : {}),
      pontos: ls.map((l) => ({ lat: l.lat, lng: l.lng })),
      ...(duas(e) ? { nomes: ls.map((l) => desenho.nomeParaDesenho(l.local)) } : {}),
      frase: fraseInfo(e).frase,
      qr: e.qr,
    };
  }

  /**
   * Porque é que o botão de compra não funciona, pela ordem em que o cliente
   * resolve: `{ bloqueado, motivo, campo, detalhe? }`. `campo` é o controlo
   * onde a página marca o erro ('peca', 'variante', 'disposicao', 'lugar',
   * 'lugar2', 'frase', 'estilo', 'estiloCostas', 'confirmar').
   * 'variante_disposicao': a variante do tema e a disposição divergem no ter
   * costas (KTD5, R14); a página pede `varianteAlvo` ao tema.
   * 'falta_estilo_costas': em 'frente_costas' não há estilo das costas que sirva.
   */
  function bloqueio(e) {
    const b = (motivo, campo, detalhe) => ({ bloqueado: true, motivo, campo, ...(detalhe ? { detalhe } : {}) });
    if (!dono(PECAS, e.peca) || !ATIVAS.includes(e.peca)) return b('peca_inativa', 'peca');
    if (e.variante !== 'ok' || !e.cor || !e.tamanho || !e.tinta) return b('variante', 'variante');
    if (e.varianteCostas !== temCostas(e)) return b('variante_disposicao', 'disposicao');
    if (!e.lugares[0]) return b('falta_lugar', 'lugar');
    if (duas(e)) {
      if (!e.lugares[1]) return b('falta_lugar2', 'lugar2');
      if (proximos(e.lugares[0], e.lugares[1])) return b('pontos_proximos', 'lugar2');
    }
    const f = fraseInfo(e);
    if (!f.valida) return b(`frase_${f.motivo}`, 'frase');
    if (comCostasCompletas(e) && !e.estiloCostas) return b('falta_estilo_costas', 'estiloCostas');
    // Cada mapa da escolha (dois em 'frente_costas'), no controlo do seu estilo.
    for (const { estilo, campo } of mapasEmUso(e)) {
      const m = estadoDoEstilo(e, estilo);
      const motivos = {
        por_carregar: 'mapa_a_carregar',
        a_carregar: 'mapa_a_carregar',
        indisponivel: m.motivo === 'latitude' ? 'mapa_latitude' : 'mapa_indisponivel',
        vazio: 'mapa_vazio',
        falhou: 'mapa_falhou',
        sem_lugar: 'mapa_indisponivel',
      };
      if (m.estado !== 'pronto') return b(motivos[m.estado] ?? 'mapa_indisponivel', campo);
    }
    const v = desenho.validarDescritor(descritor(e), { pecasAtivas: ATIVAS });
    if (!v.valido) return b('invalido', 'lugar', v.erros);
    if (!e.confirmado) return b('falta_confirmar', 'confirmar');
    return { bloqueado: false, motivo: null, campo: null };
  }

  /**
   * As propriedades da linha (o contrato de U8, em tools/coordinates/src/encomendas.js).
   * Só leva as que têm valor: a página escreve uma entrada escondida por chave.
   */
  function propriedades(e, { lingua = 'en' } = {}) {
    const p = {};
    const d = descritor(e);
    const peca = PECAS[e.peca];
    if (peca) p['Peça'] = etiquetaNaLingua(peca.etiquetas, lingua);
    const [l1, l2] = e.lugares;
    if (l1) {
      p.Lugar = l1.local;
      p.Coordenadas = formatarDMS(l1.lat, l1.lng).texto;
    }
    if (duas(e) && l2) {
      p['Lugar 2'] = l2.local;
      p['Coordenadas 2'] = formatarDMS(l2.lat, l2.lng).texto;
    }
    if (d.frase) p.Frase = d.frase;
    if (e.tinta && conf.TINTAS?.[e.tinta]) p.Estampado = etiquetaNaLingua(conf.TINTAS[e.tinta].etiquetas, lingua);
    p['Disposição'] = etiquetaNaLingua(ETIQUETAS_DISPOSICOES[disposicaoDe(e)], lingua);

    p._peca = e.peca;
    p._composicao = e.composicao;
    p._disposicao = disposicaoDe(e);
    if (d.estilo) p._estilo = d.estilo;
    if (d.pontos.length) p._pontos = `[${d.pontos.map((pt) => `[${seis(pt.lat)},${seis(pt.lng)}]`).join(',')}]`;
    if (d.frase) p._frase = d.frase;
    if (e.tinta) p._tinta = e.tinta;
    p._qr = e.qr ? 'sim' : 'nao';
    if (comMapa(e.composicao)) {
      const k = chaveMapa(e);
      const m = k ? e.mapas[k] : null;
      if (m?.versaoDados) p._dados = m.versaoDados;
      if (e.estilo === 'contorno' && m?.osm) p._osm = m.osm;
    }
    if (d.estiloCostas) {
      p._estilo_costas = d.estiloCostas;
      const k = chaveCostas(e);
      const m = k ? e.mapas[k] : null;
      if (m?.versaoDados) p._dados_costas = m.versaoDados;
      if (d.estiloCostas === 'contorno' && m?.osm) p._osm_costas = m.osm;
    }
    p._versao = conf.VERSAO_CONFIGURADOR;
    p._lingua = lingua;
    const historia = String(e.historia ?? '').trim();
    if (historia) {
      p._historia = [...historia].slice(0, HISTORIA_MAX).join('');
      if (e.publicar) p._publicar_historia = 'sim';
    }
    return p;
  }

  const memoPrevia = new Map();
  /**
   * O mapa desenhado para a pré-visualização do `lado` (forma do desenho
   * desse lado), ou null. Sem lado, o do primeiro mapa da escolha.
   */
  function mapaDaPrevia(e, lado = null) {
    const uso = mapasEmUso(e).find((m) => lado === null || m.lado === lado);
    if (!uso) return null;
    const k = chavePara(e, uso.estilo);
    const m = k ? e.mapas[k] : null;
    if (!m || m.estado !== 'pronto') return null;
    const chave = `${disposicaoDe(e)}|${uso.lado}|${e.composicao}|${k}`;
    if (memoPrevia.has(chave)) return memoPrevia.get(chave);
    const r = mapas.desenharMapa(desenho.pedidoMapa(descritor(e), m.dados, uso.lado));
    if (memoPrevia.size > 12) memoPrevia.delete(memoPrevia.keys().next().value);
    memoPrevia.set(chave, r);
    return r;
  }

  /** Os mapas da pré-visualização por lado, para `desenho.desenharLados`: `{ frente?, costas? }`. */
  function mapasDaPrevia(e) {
    const r = {};
    for (const { lado } of mapasEmUso(e)) {
      const m = mapaDaPrevia(e, lado);
      if (m) r[lado] = m;
    }
    return r;
  }

  /**
   * O que vai em cada lado, para o resumo: `{ conteudo, composicao, estilo }`,
   * com `conteudo` 'desenho' (o desenho completo), 'coordenadas' (a frente
   * pequena), 'logo' ou 'mapa' (as costas de 'frente_costas'); costas null em
   * só frente.
   */
  function lados(e) {
    const estilo = comMapa(e.composicao) ? e.estilo : null;
    const desenhoCompleto = { conteudo: 'desenho', composicao: e.composicao, estilo };
    const so = (conteudo) => ({ conteudo, composicao: null, estilo: null });
    switch (disposicaoDe(e)) {
      case 'frente_pequena_costas':
        return { frente: so('coordenadas'), costas: desenhoCompleto };
      case 'costas':
        return { frente: so('logo'), costas: desenhoCompleto };
      case 'frente_costas':
        return { frente: desenhoCompleto, costas: { conteudo: 'mapa', composicao: 'mapa', estilo: e.estiloCostas } };
      default:
        return { frente: desenhoCompleto, costas: null };
    }
  }

  /** Os nomes para o texto que resume a pré-visualização, com o que vai em cada lado. */
  function resumo(e, { lingua = 'en' } = {}) {
    const ls = lugaresEmUso(e);

    return {
      peca: etiquetaNaLingua(PECAS[e.peca]?.etiquetas, lingua),
      cor: e.cor ? etiquetaNaLingua(conf.ETIQUETAS_CORES?.[e.cor], lingua) || e.cor : '',
      tinta: e.tinta ? etiquetaNaLingua(conf.TINTAS?.[e.tinta]?.etiquetas, lingua) : '',
      composicao: e.composicao,
      estilo: comMapa(e.composicao) ? e.estilo : null,
      lugares: ls.map((l) => l.local),
      coordenadas: ls.map((l) => formatarDMS(l.lat, l.lng).texto),
      frase: fraseInfo(e).frase,
      qr: e.qr,
      disposicao: disposicaoDe(e),
      estiloCostas: comCostasCompletas(e) ? e.estiloCostas : null,
      lados: lados(e),
    };
  }

  // ===== Rascunho (KTD15: a troca de língua repõe as escolhas)

  /**
   * `v: 2` leva também `fraseIndice` e `fraseMudada` (KTD10): sem mudança, a
   * página noutra língua mostra a tradução da frase sorteada em vez de sortear
   * outra. Leva também `disposicao` e `estiloCostas` (guardado mesmo em só
   * frente). Um rascunho `v: 1` não leva disposição nem estilo das costas (fica
   * a disposição da variante), e a frase dele conta como escrita pelo cliente.
   * A disposição do rascunho só se repõe se bater com a variante do tema (KTD5:
   * a variante paga manda).
   */
  function rascunho(e) {
    return {
      v: 2,
      tinta: e.tinta,
      disposicao: disposicaoDe(e),
      composicao: e.composicao,
      estilo: e.estilo,
      estiloCostas: e.estiloCostas ?? null,
      lugares: e.lugares,
      frase: e.frase,
      fraseIndice: e.fraseIndice ?? null,
      fraseMudada: e.fraseMudada === true,
      qr: e.qr,
      historia: e.historia,
      publicar: e.publicar,
    };
  }

  /** Repõe um rascunho por cima do estado atual, valor a valor, com as mesmas regras das ações. */
  function restaurar(base, r) {
    if (!r || typeof r !== 'object' || Array.isArray(r)) return { estado: base, avisos: [] };
    const acoes = [];
    if (typeof r.tinta === 'string') acoes.push({ tipo: 'tinta', tinta: r.tinta });
    if (Array.isArray(r.lugares)) {
      r.lugares.slice(0, 2).forEach((l, indice) => {
        if (l) acoes.push({ tipo: 'lugar', indice, lugar: l });
      });
    }
    if (typeof r.estilo === 'string') acoes.push({ tipo: 'estilo', estilo: r.estilo });
    if (typeof r.composicao === 'string') acoes.push({ tipo: 'composicao', composicao: r.composicao });
    if (r.v === 2 && typeof r.estiloCostas === 'string') acoes.push({ tipo: 'estiloCostas', estilo: r.estiloCostas });
    if (r.v === 2 && DISPOSICOES.includes(r.disposicao) && (r.disposicao !== 'frente') === base.varianteCostas) {
      acoes.push({ tipo: 'disposicao', disposicao: r.disposicao });
      // A composição de 'frente' que a disposição com mapa não deixava aplicar.
      if (typeof r.composicao === 'string') acoes.push({ tipo: 'composicao', composicao: r.composicao });
    }
    const sorteada = r.v === 2 && r.fraseMudada === false;
    if (!sorteada && typeof r.frase === 'string' && r.frase.length <= 200) acoes.push({ tipo: 'frase', frase: r.frase });
    if (typeof r.qr === 'boolean') acoes.push({ tipo: 'qr', qr: r.qr });
    if (typeof r.historia === 'string') acoes.push({ tipo: 'historia', historia: r.historia.slice(0, HISTORIA_MAX * 2) });
    if (typeof r.publicar === 'boolean') acoes.push({ tipo: 'publicar', publicar: r.publicar });
    let e = base;
    // A frase sorteada nesta visita, na língua desta página.
    if (r.v === 2 && indiceValido(r.fraseIndice)) {
      e = { ...e, fraseIndice: r.fraseIndice };
      if (sorteada) e = { ...e, frase: fraseDaLista(r.fraseIndice), fraseMudada: false };
    }
    const avisos = [];
    for (const a of acoes) {
      const x = aplicar(e, a);
      e = x.estado;
      avisos.push(...x.avisos.filter((v) => v.campo !== 'confirmar'));
    }
    return { estado: { ...e, confirmado: false }, avisos };
  }

  return {
    inicial,
    aplicar,
    pedidoCarga,
    pedidosCarga,
    estadoMapa,
    estilos,
    estilosCostas,
    fraseInfo,
    descritor,
    bloqueio,
    propriedades,
    mapaDaPrevia,
    mapasDaPrevia,
    resumo,
    varianteAlvo,
    temCostas,
    exigeMapa,
    fraseNasCostas,
    disposicoes: DISPOSICOES,
    rascunho,
    restaurar,
    precisaLugar2: duas,
    comMapa,
    pecasVisiveis: () => ATIVAS.filter((p) => dono(PECAS, p)),
    pecas: PECAS,
  };
}
